import React, { useState, useEffect, useRef } from "react";
import { FiGlobe, FiLoader, FiCheck, FiChevronDown } from "react-icons/fi";
import StableLanguageIcon from "./StableLanguageIcon";
import "./GoogleTranslate.css"; // Dùng chung CSS

// Thêm tham số isSidebar để xác định component được sử dụng trong sidebar
// Thêm tham số isInHeader để xác định khi component được sử dụng trong header
const LanguageSelector = ({ isSidebar, isSidebarCollapsed, isInHeader }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState("Tiếng Việt");
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [errorDetected, setErrorDetected] = useState(false);
  const [isChangingLanguage, setIsChangingLanguage] = useState(false);
  const [selectedLangCode, setSelectedLangCode] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [showRipple, setShowRipple] = useState(false);
  const [safeAreaBottom, setSafeAreaBottom] = useState(10);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null); // Ref cho button để tính toán vị trí
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const dropdownWidth = 220; // Chiều rộng cố định của dropdown

  const languages = [
    { code: "vi", name: "Tiếng Việt", displayName: "Tiếng Việt" },
    { code: "en", name: "English", displayName: "English" },
    { code: "ko", name: "한국어", displayName: "한국어" },
    { code: "ja", name: "日本語", displayName: "日本語" },
    { code: "zh-CN", name: "中文", displayName: "中文" },
    { code: "th", name: "ไทย", displayName: "ไทย" },
  ];

  const renderLanguageIcon = (language, size = "sm") => (
    <StableLanguageIcon
      code={language.code}
      label={language.displayName || language.name}
      size={size}
    />
  );

  useEffect(() => {
    // Biến để kiểm soát lỗi lặp vô hạn
    let errorCount = 0;
    const maxErrors = 3;

    // Xử lý khi người dùng click "Turn off translation" trên thanh Google
    const handleMessage = (event) => {
      if (event.data && event.data.action === "gtranslateBannerDismissed") {
        // User đã click "Turn off translation"
        hideGoogleElements();
      }
    };

    window.addEventListener("message", handleMessage);

    // Ghi đè console.error để bắt lỗi
    const originalConsoleError = console.error;
    console.error = function () {
      // Kiểm tra lỗi "Cannot read properties of undefined (reading 'J')"
      if (
        arguments[0] &&
        typeof arguments[0] === "string" &&
        (arguments[0].includes(
          "Cannot read properties of undefined (reading 'J')"
        ) ||
          arguments[0].includes(
            "TypeError: Cannot read properties of undefined"
          ))
      ) {
        errorCount++;

        if (errorCount >= maxErrors && !errorDetected) {
          setErrorDetected(true);
          console.log(
            "Đã phát hiện lỗi lặp vô hạn Google Translate, đang reset..."
          );

          // Reset Google Translate và chuyển về ngôn ngữ gốc
          try {
            document.cookie =
              "googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
            document.cookie =
              "googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=." +
              window.location.hostname;

            // Xóa script và element
            const script = document.getElementById("google-translate-script");
            if (script) script.remove();

            const element = document.getElementById("google_translate_element");
            if (element) element.innerHTML = "";

            // Reload trang sau 2 giây để tránh vòng lặp
            setTimeout(() => {
              window.location.reload();
            }, 1000);
          } catch (e) {
            // Nếu tất cả thất bại, reload ngay lập tức
            window.location.reload();
          }
          return;
        }
      }
      originalConsoleError.apply(console, arguments);
    };

    // Tạo element cho Google Translate
    if (!document.getElementById("google_translate_element")) {
      const div = document.createElement("div");
      div.id = "google_translate_element";
      div.style.display = "none";
      document.body.appendChild(div);
    }

    // Biến để theo dõi số lần thử lại
    let retryCount = 0;
    const maxRetries = 3;

    // Hàm tải Google Translate
    const loadGoogleTranslate = () => {
      // Thêm script Google Translate
      if (
        !document.getElementById("google-translate-script") &&
        !isScriptLoaded
      ) {
        try {
          // Khởi tạo hàm callback
          window.googleTranslateElementInit = () => {
            try {
              if (window.google && window.google.translate) {
                // Sử dụng cấu hình đơn giản nhất để tránh lỗi
                new window.google.translate.TranslateElement(
                  {
                    pageLanguage: "vi",
                    includedLanguages: "en,vi,ko,ja,zh-CN,th",
                    autoDisplay: false,
                    // Không sử dụng thuộc tính layout để tránh lỗi
                  },
                  "google_translate_element"
                );
                setIsScriptLoaded(true);

                // Ẩn thanh thông báo dịch
                hideGoogleElements();

                // Kiểm tra ngôn ngữ hiện tại
                setTimeout(checkCurrentLanguage, 1000);
              }
            } catch (error) {
              console.error("Error initializing Google Translate:", error);
              if (retryCount < maxRetries) {
                retryCount++;
                setTimeout(loadGoogleTranslate, 1500);
              }
            }
          };

          // Thêm script
          const script = document.createElement("script");
          script.id = "google-translate-script";
          script.src = `//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit&nocache=${Date.now()}`;
          script.async = true;
          script.onerror = (error) => {
            console.error("Error loading Google Translate script:", error);
            if (retryCount < maxRetries) {
              retryCount++;
              setTimeout(loadGoogleTranslate, 1500);
            }
          };
          document.body.appendChild(script);
        } catch (error) {
          console.error("Error adding Google Translate script:", error);
          if (retryCount < maxRetries) {
            retryCount++;
            setTimeout(loadGoogleTranslate, 1500);
          }
        }
      }
    };

    // Bắt đầu tải Google Translate sau một khoảng thời gian
    if (!errorDetected) {
      setTimeout(loadGoogleTranslate, 1000);
    }

    // Kiểm tra và ẩn thanh thông báo dịch theo định kỳ
    const hideInterval = setInterval(hideGoogleElements, 1000);

    return () => {
      clearInterval(hideInterval);
      window.removeEventListener("message", handleMessage);
      // Khôi phục console.error gốc để tránh ảnh hưởng đến các component khác
      console.error = originalConsoleError;
    };
  }, [isScriptLoaded, errorDetected]);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    // Kiểm tra ngay khi component mount
    checkMobile();

    // Thêm event listener để kiểm tra khi resize
    window.addEventListener("resize", checkMobile);

    // Cleanup
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Thêm event listener để đóng dropdown khi click ra ngoài
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Hàm ẩn các phần tử của Google Translate
  const hideGoogleElements = () => {
    // Ẩn thanh thông báo
    const banner = document.querySelector(".goog-te-banner-frame");
    if (banner) {
      banner.style.display = "none";
    }

    // Ẩn các phần tử khác có thể xuất hiện
    const elements = document.querySelectorAll(
      ".VIpgJd-ZVi9od-aZ2wEe-wOHMyf, .VIpgJd-ZVi9od-aZ2wEe-OiiCO, .skiptranslate, .goog-tooltip"
    );
    elements.forEach((el) => {
      if (el) {
        el.style.display = "none";
      }
    });

    // Loại bỏ phần đánh dấu văn bản được dịch
    const highlights = document.querySelectorAll(".goog-text-highlight");
    highlights.forEach((el) => {
      if (el) {
        el.classList.remove("goog-text-highlight");
      }
    });

    // Sửa lại vị trí của body
    document.body.style.top = "0px";
    document.body.style.position = "static";

    // Thêm CSS trực tiếp vào trang để đảm bảo thanh không hiển thị
    if (!document.getElementById("gt-hide-styles")) {
      const style = document.createElement("style");
      style.id = "gt-hide-styles";
      style.innerHTML = `
        .goog-te-banner-frame, .VIpgJd-ZVi9od-aZ2wEe-wOHMyf, .VIpgJd-ZVi9od-aZ2wEe-OiiCO, .skiptranslate, .goog-tooltip {
          display: none !important;
        }
        body {
          top: 0 !important;
          position: static !important;
        }
      `;
      document.head.appendChild(style);
    }
  };

  const checkCurrentLanguage = () => {
    const cookieValue = document.cookie
      .split("; ")
      .find((row) => row.startsWith("googtrans="));

    if (cookieValue) {
      const langCode = cookieValue.split("/").pop();
      const language = languages.find((lang) => lang.code === langCode);
      if (language) {
        setCurrentLanguage(language.displayName);
      }
    }

    // Ẩn lại các phần tử của Google sau khi kiểm tra ngôn ngữ
    hideGoogleElements();
  };

  const changeLanguage = (langCode) => {
    if (errorDetected) {
      alert("Đã phát hiện lỗi với Google Translate. Vui lòng tải lại trang.");
      return;
    }

    // Hiển thị trạng thái đang xử lý
    setIsChangingLanguage(true);
    setSelectedLangCode(langCode);
    setIsOpen(false); // Đóng dropdown ngay khi chọn

    try {
      // Tìm ngôn ngữ theo code và lấy displayName
      const language = languages.find((lang) => lang.code === langCode);

      // Sử dụng phương pháp cookie trực tiếp (đáng tin cậy hơn)
      // Xóa cookie hiện tại
      document.cookie =
        "googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
      document.cookie =
        "googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=." +
        window.location.hostname;

      // Tạo cookie mới với ngôn ngữ được chọn
      const cookieValue = `/auto/${langCode}`;
      document.cookie = `googtrans=${cookieValue}; path=/;`;
      document.cookie = `googtrans=${cookieValue}; path=/; domain=.${window.location.hostname};`;

      // Cập nhật state với displayName không được dịch
      if (language) {
        // Không cập nhật currentLanguage ngay để giữ hiệu ứng loading
        // Chỉ lưu vào localStorage
        localStorage.setItem(
          "selectedLanguageDisplayName",
          language.displayName
        );
        localStorage.setItem("selectedLanguageCode", language.code);
      }

      // Ẩn thanh thông báo dịch
      hideGoogleElements();

      // Tạo hiệu ứng loading trước khi reload trang
      // Sau 800ms sẽ bắt đầu reload trang, cho người dùng thấy hiệu ứng loading
      setTimeout(() => {
        window.location.reload();
      }, 800);
    } catch (error) {
      console.error("Error changing language:", error);
      // Nếu có lỗi, hủy hiệu ứng loading
      setIsChangingLanguage(false);
      // Fallback: reload trang
      window.location.reload();
    }
  };

  // Thêm chức năng trở về ngôn ngữ gốc
  const resetToOriginalLanguage = () => {
    try {
      // Hiển thị trạng thái đang xử lý
      setIsChangingLanguage(true);
      setSelectedLangCode("vi");
      setIsOpen(false); // Đóng dropdown ngay khi chọn

      // Xóa cookie googtrans
      document.cookie =
        "googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
      document.cookie =
        "googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=." +
        window.location.hostname;

      // Xóa lưu trữ localStorage
      localStorage.removeItem("selectedLanguageDisplayName");
      localStorage.removeItem("selectedLanguageCode");

      // Đặt lại tiếng Việt làm ngôn ngữ mặc định
      // Không cập nhật currentLanguage ngay để giữ hiệu ứng loading

      // Tạo hiệu ứng loading trước khi reload trang
      setTimeout(() => {
        window.location.reload();
      }, 800);
    } catch (error) {
      console.error("Error resetting language:", error);
      // Nếu có lỗi, hủy hiệu ứng loading
      setIsChangingLanguage(false);
      window.location.reload();
    }
  };

  // Kiểm tra và sử dụng giá trị đã lưu trong localStorage khi component mount
  useEffect(() => {
    const savedDisplayName = localStorage.getItem(
      "selectedLanguageDisplayName"
    );
    const savedLangCode = localStorage.getItem("selectedLanguageCode");

    if (savedDisplayName) {
      setCurrentLanguage(savedDisplayName);
    }

    if (savedLangCode) {
      setSelectedLangCode(savedLangCode);
    } else {
      setSelectedLangCode("vi"); // Mặc định là tiếng Việt
    }
  }, []);

  // Thêm hiệu ứng ripple khi click vào nút
  const handleButtonClick = () => {
    if (isChangingLanguage) return;

    // Tính toán vị trí chỉ khi mở dropdown
    if (!isOpen) {
      if (isSidebar && isSidebarCollapsed && buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();

        // Kiểm tra nếu dropdown sẽ tràn ra ngoài màn hình bên phải
        const rightEdge = rect.right + 10 + dropdownWidth;
        const windowWidth = window.innerWidth;

        if (rightEdge > windowWidth) {
          // Nếu tràn ra ngoài bên phải, hiển thị bên trái
          setDropdownPosition({
            top: rect.top - 100, // Hiển thị dropdown cao hơn một chút
            left: Math.max(10, rect.left - dropdownWidth - 10), // Bên trái button với margin 10px
          });
        } else {
          // Nếu không tràn, hiển thị bên phải như bình thường
          setDropdownPosition({
            top: rect.top - 100, // Hiển thị dropdown cao hơn một chút
            left: rect.right + 10, // Bên phải button với margin 10px
          });
        }
      }
    }

    // Hiệu ứng ripple cho mobile
    if (isMobile) {
      setShowRipple(true);
      setTimeout(() => {
        setShowRipple(false);
      }, 800);
    }

    setIsOpen(!isOpen);
  };

  // Kiểm tra safe area cho iOS
  useEffect(() => {
    const checkSafeArea = () => {
      // Trên iOS, env(safe-area-inset-bottom) là giá trị an toàn
      // Trên các thiết bị khác, chúng ta sẽ sử dụng giá trị mặc định
      if (CSS.supports("padding-bottom: env(safe-area-inset-bottom)")) {
        // Lấy giá trị safe area từ CSS variables
        const safeArea = parseInt(
          getComputedStyle(document.documentElement)
            .getPropertyValue("--safe-area-bottom")
            .trim() || "0"
        );

        setSafeAreaBottom(Math.max(safeArea, 10)); // Tối thiểu là 10px
      }
    };

    // Thêm CSS variable vào document
    document.documentElement.style.setProperty(
      "--safe-area-bottom",
      "env(safe-area-inset-bottom, 10px)"
    );

    checkSafeArea();

    // Kiểm tra lại khi resize
    window.addEventListener("resize", checkSafeArea);

    return () => window.removeEventListener("resize", checkSafeArea);
  }, []);

  // Đóng dropdown khi scroll
  useEffect(() => {
    const handleScroll = () => {
      if (isOpen) {
        setIsOpen(false);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => window.removeEventListener("scroll", handleScroll);
  }, [isOpen]);

  // Nếu đã phát hiện lỗi, hiển thị thông báo thay vì widget
  if (errorDetected) {
    return (
      <div
        style={{ position: "fixed", top: "10px", right: "10px", zIndex: 1001 }}
      >
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-2 rounded-md">
          Lỗi Google Translate. Đang tải lại...
        </div>
      </div>
    );
  }

  // Lấy ngôn ngữ hiện tại
  const getCurrentLanguage = () => {
    return (
      languages.find((lang) => lang.code === selectedLangCode) || languages[0]
    );
  };

  // Tạo một div riêng cho tên ngôn ngữ để không bị dịch
  if (isInHeader) {
    // UI cho header landing page
    return (
      <div className="relative" ref={dropdownRef}>
        <button
          ref={buttonRef}
          onClick={handleButtonClick}
          disabled={isChangingLanguage}
          className="flex items-center text-white"
          aria-label="Chuyển đổi ngôn ngữ"
        >
          <span className="flag-icon flex items-center">
            {renderLanguageIcon(getCurrentLanguage(), "sm")}
          </span>
          <FiChevronDown className="ml-1 text-xs" />
        </button>

        {isOpen && !isChangingLanguage && (
          <div
            className="absolute bg-white border border-gray-200 rounded-md shadow-lg py-1 notranslate dropdown-animation right-0 top-full mt-2 w-56"
            style={{ zIndex: 2000 }}
          >
            {/* <div className="py-2 px-4 text-xs text-gray-500 border-b border-gray-100">
              Chọn ngôn ngữ
            </div> */}
            {languages.map((language) => (
              <button
                key={language.code}
                className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 transition-colors duration-200 ${
                  language.code === selectedLangCode
                    ? "bg-blue-50 text-blue-600 font-medium"
                    : ""
                }`}
                onClick={() => changeLanguage(language.code)}
              >
                <div className="flex items-center">
                  <span className="flag-icon mr-2">
                    {renderLanguageIcon(language, "sm")}
                  </span>
                  <span>{language.displayName}</span>
                  {language.code === selectedLangCode && (
                    <FiCheck className="ml-auto text-blue-600" />
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // UI cho sidebar
  if (isSidebar) {
    // UI cho sidebar
    const currentLang = getCurrentLanguage();

    return (
      <div className="relative" ref={dropdownRef}>
        <button
          ref={buttonRef} // Thêm ref vào button
          onClick={handleButtonClick}
          disabled={isChangingLanguage}
          className={`text-white rounded flex items-center w-full ${
            isSidebarCollapsed
              ? "justify-center px-1 py-3"
              : "justify-start p-2"
          } hover:bg-gray-700`}
        >
          <FiGlobe
            className={`text-2xl ${!isSidebarCollapsed ? "mr-2" : ""}`}
          />
          {!isSidebarCollapsed && (
            <span className="flex items-center">
              {isChangingLanguage ? "Đang chuyển..." : "Ngôn ngữ"}
              <span className="ml-2 text-xs px-2 py-0.5 bg-blue-500 rounded-full flex items-center">
                {renderLanguageIcon(currentLang, "sm")}
              </span>
            </span>
          )}
        </button>

        {isOpen && !isChangingLanguage && (
          <div
            className="bg-gray-700 border border-gray-600 rounded-md shadow-lg py-1 notranslate"
            style={{
              position: isSidebarCollapsed ? "fixed" : "absolute",
              maxHeight: "300px",
              overflowY: "auto",
              zIndex: 2000, // Sử dụng z-index cao hơn để đảm bảo hiển thị trên tất cả
              width: dropdownWidth + "px",
              ...(isSidebarCollapsed
                ? {
                    top: dropdownPosition.top + "px",
                    left: dropdownPosition.left + "px",
                  }
                : {
                    bottom: "100%",
                    left: "0",
                    right: "0",
                    marginBottom: "4px",
                  }),
            }}
          >
            {/* <div className="py-2 px-4 text-xs text-gray-300 border-b border-gray-600">
              Chọn ngôn ngữ
            </div> */}
            {languages.map((language) => (
              <button
                key={language.code}
                className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-600 transition-colors duration-200 text-white ${
                  language.code === selectedLangCode
                    ? "bg-gray-600 font-medium"
                    : ""
                }`}
                onClick={() => changeLanguage(language.code)}
              >
                <div className="flex items-center">
                  <span className="flag-icon mr-2">
                    {renderLanguageIcon(language, "sm")}
                  </span>
                  <span>{language.displayName}</span>
                  {language.code === selectedLangCode && (
                    <FiCheck className="ml-auto text-blue-400" />
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // UI cho nút floating
  // Lấy tên ngôn ngữ hiển thị cho nút
  const getButtonContent = () => {
    const currentLang = getCurrentLanguage();

    if (isChangingLanguage) {
      return (
        <>
          {!isMobile && (
            <span className="flag-icon mr-1 mt-1 flex items-center">
              {renderLanguageIcon(currentLang, "sm")}
            </span>
          )}
          <span
            className={`notranslate ${
              isMobile ? "sr-only" : "ml-1"
            } flex items-center`}
          >
            {currentLanguage}
          </span>
        </>
      );
    } else {
      return (
        <>
          {!isMobile && (
            <span className="flag-icon mr-1 mt-1 flex items-center">
              {renderLanguageIcon(currentLang, "sm")}
            </span>
          )}
          <FiGlobe
            className={`${
              isMobile ? "mr-0" : "hidden"
            } flex-shrink-0 self-center text-xl`}
          />
          <span
            className={`notranslate ${
              isMobile ? "sr-only" : "ml-1"
            } flex items-center`}
          >
            {currentLanguage}
          </span>
        </>
      );
    }
  };

  // UI cho nút floating mặc định
  return (
    <div
      className="relative"
      ref={dropdownRef}
      style={{
        zIndex: 1001,
        position: "fixed",
        top: isMobile ? "auto" : "10px",
        bottom: isMobile ? `${safeAreaBottom}px` : "auto",
        right: "10px",
      }}
    >
      <button
        className={`flex items-center justify-center ${
          isMobile
            ? "language-button-mobile bg-blue-600 text-white"
            : "rounded-md px-2 py-1 text-sm transition-all duration-300 language-button-desktop"
        }`}
        onClick={handleButtonClick}
        disabled={isChangingLanguage}
        aria-label="Chuyển đổi ngôn ngữ"
      >
        {showRipple && isMobile && <span className="ripple-effect"></span>}
        <div className="flex items-center">{getButtonContent()}</div>
      </button>

      {isOpen && !isChangingLanguage && (
        <div
          className={`
          absolute bg-white border border-gray-200 rounded-md shadow-lg py-1 notranslate dropdown-animation
          ${
            isMobile
              ? "language-dropdown-mobile top-full mt-2 right-0 left-auto w-48"
              : "language-dropdown-desktop right-0 top-full mt-2 w-56"
          }
        `}
        >
          {/* <div className="py-2 px-4 text-xs text-gray-500 border-b border-gray-100">
            Chọn ngôn ngữ
          </div> */}
          {languages.map((language) => (
            <button
              key={language.code}
              className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 transition-colors duration-200 ${
                language.code === selectedLangCode
                  ? "bg-blue-50 text-blue-600 font-medium"
                  : ""
              } ${
                isMobile ? "language-option-mobile" : "language-option-desktop"
              }`}
              onClick={() => changeLanguage(language.code)}
            >
              <div className="flex items-center">
                <span className="flag-icon mr-2">
                  {renderLanguageIcon(language, "sm")}
                </span>
                <span>{language.displayName}</span>
                {language.code === selectedLangCode && (
                  <FiCheck className="ml-auto text-blue-600" />
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
